import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../lib/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tenantContext, setTenantContext] = useState(null);
  const [availableTenants, setAvailableTenants] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const savedUser = localStorage.getItem('user');
    
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      checkAuth();
    } else {
      setLoading(false);
    }
  }, []);

  const checkAuth = async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
      
      // If super_admin, fetch tenant context and available tenants
      if (response.data.role === 'super_admin' || response.data.is_super_admin) {
        fetchTenantContext();
        fetchAvailableTenants();
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const fetchTenantContext = async () => {
    try {
      const response = await api.get('/tenants/me/context');
      setTenantContext(response.data);
    } catch (error) {
      console.error('Failed to fetch tenant context:', error);
    }
  };

  const fetchAvailableTenants = async () => {
    try {
      const response = await api.get('/tenants');
      setAvailableTenants(response.data.tenants || []);
    } catch (error) {
      console.error('Failed to fetch tenants:', error);
    }
  };

  const startImpersonation = async (tenantId) => {
    try {
      const response = await api.post('/tenants/impersonate', { tenant_id: tenantId });
      if (response.data.success) {
        await fetchTenantContext();
        // Refresh the page to reload all data with new tenant context
        window.location.reload();
      }
      return response.data;
    } catch (error) {
      console.error('Impersonation failed:', error);
      throw error;
    }
  };

  const stopImpersonation = async () => {
    try {
      const response = await api.post('/tenants/impersonate/stop');
      if (response.data.success) {
        await fetchTenantContext();
        window.location.reload();
      }
      return response.data;
    } catch (error) {
      console.error('Stop impersonation failed:', error);
      throw error;
    }
  };

  const login = async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    
    // If super_admin, fetch tenant context immediately
    if (userData.role === 'super_admin' || userData.is_super_admin) {
      // Fetch immediately after setting user
      try {
        const contextRes = await api.get('/tenants/me/context');
        setTenantContext(contextRes.data);
        
        const tenantsRes = await api.get('/tenants');
        setAvailableTenants(tenantsRes.data.tenants || []);
      } catch (err) {
        console.error('Failed to fetch tenant data on login:', err);
      }
    }
    
    return userData;
  };

  const register = async (email, password, full_name, role) => {
    const response = await api.post('/auth/register', { email, password, full_name, role });
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    setTenantContext(null);
    setAvailableTenants([]);
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading, 
      login, 
      register, 
      logout,
      // Multi-tenant additions
      tenantContext,
      availableTenants,
      startImpersonation,
      stopImpersonation,
      refreshTenantContext: fetchTenantContext
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};